import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Search, Phone, MessageCircle, ChevronRight, UserCheck, CheckSquare, Square, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import LeadDetail from './LeadDetail';

const CLIENT_STAGES = [
  'Enrolled', 'Intimation Letter Sent', 'ECS Cancellation Processing',
  'WhatsApp Group Created', 'Agreement Signed', 'Preferred Location Sent',
  'Harassment Complaint Filed', 'Settlement Request Sent',
  'Conciliation Notice Sent', 'Arbitration Notice Sent',
  'Legal Notice Replied', 'Case Closed',
];

function stageBadge(stage) {
  const map = {
    'Enrolled': 'badge-green', 'Intimation Letter Sent': 'badge-blue',
    'ECS Cancellation Processing': 'badge-yellow', 'WhatsApp Group Created': 'badge-purple',
    'Agreement Signed': 'badge-green', 'Preferred Location Sent': 'badge-blue',
    'Harassment Complaint Filed': 'badge-red', 'Settlement Request Sent': 'badge-purple',
    'Conciliation Notice Sent': 'badge-yellow', 'Arbitration Notice Sent': 'badge-yellow',
    'Legal Notice Replied': 'badge-blue', 'Case Closed': 'badge-gray',
  };
  return map[stage] || 'badge-gray';
}

function fmt(n) { return n ? '₹' + Number(n).toLocaleString('en-IN') : '—'; }

// Bulk allocate modal
function BulkAllocateModal({ selectedIds, clients, onClose, onSave }) {
  const [consultants, setConsultants] = useState([]);
  const [assignTo, setAssignTo] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/users').then(r => setConsultants(r.data.filter(u => u.role === 'consultant' && u.active !== false)));
  }, []);

  const save = async () => {
    if (!assignTo) return toast.error('Consultant select karo');
    setSaving(true);
    try {
      await Promise.all(selectedIds.map(id => api.patch(`/leads/${id}`, { assignedTo: assignTo })));
      toast.success(`${selectedIds.length} clients reallocated!`);
      onSave();
    } catch (e) {
      toast.error('Failed');
    } finally {
      setSaving(false);
    }
  };

  const selectedClients = clients.filter(c => selectedIds.includes(c.id));

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="modal-title">🔀 Bulk Client Reallocation</div>
          <button className="btn-icon" onClick={onClose}><span>✕</span></button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 16, padding: '10px 12px', background: 'var(--surface)', borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{selectedIds.length} clients selected:</div>
            <div style={{ maxHeight: 150, overflowY: 'auto' }}>
              {selectedClients.map(c => (
                <div key={c.id} style={{ fontSize: 12, padding: '3px 0', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
                  {c.name} — {c.stage}
                </div>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Assign to Consultant *</label>
            <select className="form-control" value={assignTo} onChange={e => setAssignTo(e.target.value)}>
              <option value="">Select consultant...</option>
              {consultants.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" /> : `Reallocate ${selectedIds.length} Clients`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Direct Enroll Modal (for CRM migration - Admin/TL only)
function DirectEnrollModal({ users, onClose, onSave }) {
  const CLIENT_STAGES_LOCAL = [
    'Enrolled', 'Intimation Letter Sent', 'ECS Cancellation Processing',
    'WhatsApp Group Created', 'Agreement Signed', 'Preferred Location Sent',
    'Settlement Request Sent', 'Conciliation Notice Sent', 'Arbitration Notice Sent', 'Legal Notice Replied'
  ];
  const [form, setForm] = useState({
    name: '', mobile: '', email: '', city: '', alternateWhatsApp: '',
    personalLoan: '', creditCard: '', employmentStatus: '', monthlyFee: '',
    enrollmentDate: new Date().toISOString().split('T')[0],
    clientStage: 'Enrolled', assignedTo: ''
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const consultants = users.filter(u => u.role === 'consultant' && u.active !== false);

  const save = async () => {
    if (!form.name || !form.mobile) return toast.error('Name aur mobile required');
    setSaving(true);
    try {
      await api.post('/leads/direct-enroll', form);
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <div className="modal-title">➕ Add Enrolled Client (CRM Import)</div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ padding: '8px 12px', background: '#eff6ff', borderRadius: 8, fontSize: 12, color: '#1d4ed8', marginBottom: 16 }}>
            ℹ️ Yeh option sirf existing clients import karne ke liye hai jo pehle se enrolled hain.
          </div>
          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Mobile *</label>
              <input className="form-control" value={form.mobile} onChange={e => set('mobile', e.target.value)} maxLength={10} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-control" value={form.city} onChange={e => set('city', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Personal Loan (₹)</label>
              <input className="form-control" type="number" value={form.personalLoan} onChange={e => set('personalLoan', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Credit Card (₹)</label>
              <input className="form-control" type="number" value={form.creditCard} onChange={e => set('creditCard', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Monthly Fee (₹)</label>
              <input className="form-control" type="number" value={form.monthlyFee} onChange={e => set('monthlyFee', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Enrollment Date</label>
              <input className="form-control" type="date" value={form.enrollmentDate} onChange={e => set('enrollmentDate', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Current Stage</label>
              <select className="form-control" value={form.clientStage} onChange={e => set('clientStage', e.target.value)}>
                {CLIENT_STAGES_LOCAL.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Assign to Consultant</label>
              <select className="form-control" value={form.assignedTo} onChange={e => set('assignedTo', e.target.value)}>
                <option value="">Select consultant...</option>
                {consultants.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" /> : '➕ Add Enrolled Client'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EnrolledClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [showAllocate, setShowAllocate] = useState(false);
  const [showDirectEnroll, setShowDirectEnroll] = useState(false);
  const [terminateData, setTerminateData] = useState(null); // { client, waMessage, waLink, warning }
  const [showTerminated, setShowTerminated] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState('');
  const { user } = useAuth();
  const canAllocate = ['admin', 'tl'].includes(user?.role);

  const load = useCallback(() => {
    setLoading(true);
    setSelected([]);
    const params = { enrolled: 'true' };
    if (search) params.search = search;
    if (filterEmployee) params.assignedTo = filterEmployee;
    if (showTerminated) params.terminated = 'true';
    api.get('/leads', { params }).then(r => setClients(r.data)).finally(() => setLoading(false));
  }, [search, filterEmployee, showTerminated]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/users').then(r => setUsers(r.data)).catch(() => {}); }, []);

  const filtered = stage ? clients.filter(c => c.stage === stage) : clients;
  const consultants = users.filter(u => u.role === 'consultant');

  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAll = () => setSelected(selected.length === filtered.length ? [] : filtered.map(c => c.id));

  if (selectedId) {
    return <LeadDetail leadId={selectedId} onBack={() => { setSelectedId(null); load(); }} users={users} isClientView={true} clientStages={CLIENT_STAGES} />;
  }

  return (
    <div className="page">
      <div className="flex-between mb-24">
        <div>
          <h2 style={{ fontSize: 18 }}>Enrolled Clients</h2>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{filtered.length} clients</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`btn btn-sm ${showTerminated ? 'btn-danger' : 'btn-ghost'}`} onClick={() => setShowTerminated(!showTerminated)}>
            {showTerminated ? '🔴 Terminated' : 'Show Terminated'}
          </button>
          {canAllocate && selected.length > 0 && (
            <button className="btn btn-gold" onClick={() => setShowAllocate(true)}>
              <UserCheck size={15} /> Reallocate {selected.length}
            </button>
          )}
          {canAllocate && (
            <button className="btn btn-primary" onClick={() => setShowDirectEnroll(true)}>
              <Plus size={15} /> Add Client
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-16" style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="input-icon-wrap" style={{ flex: 1, minWidth: 180 }}>
            <Search size={15} />
            <input className="form-control" placeholder="Search name, mobile..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="form-control" style={{ width: 200 }} value={stage} onChange={e => setStage(e.target.value)}>
            <option value="">All Client Stages</option>
            {CLIENT_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {canAllocate && (
            <select className="form-control" style={{ width: 180 }} value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)}>
              <option value="">👥 All Consultants</option>
              {consultants.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          {selected.length > 0 && <span style={{ fontSize: 13, color: 'var(--gold)', fontWeight: 600 }}>{selected.length} selected</span>}
        </div>
      </div>

      {/* Stage pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        <button className={`btn btn-sm ${!stage ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setStage('')}>All</button>
        {CLIENT_STAGES.map(s => (
          <button key={s} className={`btn btn-sm ${stage === s ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setStage(stage === s ? '' : s)} style={{ fontSize: 11 }}>{s}</button>
        ))}
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><h3>No enrolled clients found</h3></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {canAllocate && (
                    <th style={{ width: 40 }}>
                      <button className="btn-icon" style={{ border: 'none', background: 'none' }} onClick={toggleAll}>
                        {selected.length === filtered.length && filtered.length > 0
                          ? <CheckSquare size={15} style={{ color: 'var(--navy)' }} />
                          : <Square size={15} />}
                      </button>
                    </th>
                  )}
                  <th>Client</th>
                  <th>Mobile</th>
                  <th>Liability</th>
                  <th>Monthly Fee</th>
                  <th>Enrolled On</th>
                  <th>Client Stage</th>
                  <th>Assigned To</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(client => (
                  <tr key={client.id} style={{ background: selected.includes(client.id) ? '#eff6ff' : '' }}>
                    {canAllocate && (
                      <td onClick={e => e.stopPropagation()}>
                        <button className="btn-icon" style={{ border: 'none', background: 'none' }} onClick={() => toggleSelect(client.id)}>
                          {selected.includes(client.id)
                            ? <CheckSquare size={15} style={{ color: 'var(--navy)' }} />
                            : <Square size={15} />}
                        </button>
                      </td>
                    )}
                    <td onClick={() => setSelectedId(client.id)} style={{ cursor: 'pointer' }}>
                      <div style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {client.name}
                        {client.overdueRetainer && <span title="Retainer overdue" style={{ color: 'var(--danger)' }}>🔴</span>}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{client.city}</div>
                    </td>
                    <td style={{ fontSize: 13 }}>{client.mobile}</td>
                    <td style={{ fontSize: 13, fontWeight: 600 }}>{fmt(client.totalLiability)}</td>
                    <td style={{ fontSize: 13 }}>{fmt(client.monthlyFee)}<span style={{ fontSize: 11, color: 'var(--text-muted)' }}>/mo</span></td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{client.enrollmentDate || '—'}</td>
                    <td><span className={`badge ${stageBadge(client.stage)}`}>{client.stage}</span></td>
                    <td style={{ fontSize: 12 }}>{client.assignedToName || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <a href={`tel:${client.mobile}`} className="btn-icon" onClick={e => e.stopPropagation()}><Phone size={13} /></a>
                        <a href={`https://wa.me/91${client.mobile}`} target="_blank" rel="noreferrer" className="btn-icon" style={{ color: '#25d366' }} onClick={e => e.stopPropagation()}><MessageCircle size={13} /></a>
                        <button className="btn-icon" onClick={() => setSelectedId(client.id)}><ChevronRight size={13} /></button>
                        {!client.terminated && canAllocate && (
                          <button className="btn-icon" title="Terminate" style={{ color: 'var(--danger)' }}
                            onClick={e => {
                              e.stopPropagation();
                              const reason = window.prompt('Termination reason:', 'Client stopped responding');
                              if (reason !== null) {
                                api.post(`/leads/${client.id}/terminate`, { reason }).then(r => {
                                  setTerminateData({ client, ...r.data });
                                  load();
                                });
                              }
                            }}>
                            🔴
                          </button>
                        )}
                        {client.terminated && (
                          <button className="btn btn-sm" style={{ background: '#10b981', color: 'white', fontSize: 11 }}
                            onClick={e => {
                              e.stopPropagation();
                              const reason = window.prompt('Revival reason:', 'Client wants to continue');
                              if (reason !== null) {
                                api.post(`/leads/${client.id}/revive-client`, { reason }).then(r => {
                                  toast.success(r.data.message);
                                  load();
                                }).catch(err => toast.error('Failed'));
                              }
                            }}>
                            ♻️ Revive
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAllocate && (
        <BulkAllocateModal
          selectedIds={selected}
          clients={filtered}
          onClose={() => setShowAllocate(false)}
          onSave={() => { setShowAllocate(false); load(); }}
        />
      )}

      {/* Termination WhatsApp modal */}
      {terminateData && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <div className="modal-title">🔴 Client Terminated — {terminateData.client?.name}</div>
              <button className="btn-icon" onClick={() => setTerminateData(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {terminateData.warning && (
                <div style={{ padding: '10px 12px', background: '#fff7ed', borderRadius: 8, fontSize: 12, color: '#92400e', marginBottom: 16 }}>
                  {terminateData.warning}
                </div>
              )}
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>WhatsApp termination message ready — review aur send karo:</div>
              <textarea className="form-control" rows={12} defaultValue={terminateData.waMessage} style={{ fontFamily: 'inherit', fontSize: 13, lineHeight: 1.7 }} id="wa-msg-area" />
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setTerminateData(null)}>Close</button>
              <button className="btn btn-whatsapp" onClick={() => {
                const msg = document.getElementById('wa-msg-area').value;
                window.open(`https://wa.me/91${terminateData.client?.mobile}?text=${encodeURIComponent(msg)}`, '_blank');
                setTerminateData(null);
                toast.success('WhatsApp opened!');
              }}>
                <MessageCircle size={14} /> Send via WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Direct Enroll Modal */}
      {showDirectEnroll && (
        <DirectEnrollModal
          users={users}
          onClose={() => setShowDirectEnroll(false)}
          onSave={() => { setShowDirectEnroll(false); load(); toast.success('Client enrolled!'); }}
        />
      )}
    </div>
  );
}
